import { Position } from '../entities/Transform/Position';
import { Text } from '../entities/Drawable/Text';
import { Image } from '../entities/Drawable/Image';
import { Vector } from '../entities/Vector';
import { ImageSVG } from '../entities/Drawable/ImageSVG';
import { Historic } from '../types/Historic';
import { Media } from '../entities/Media';
import { CustomGraphicElement } from '../entities/Drawable/CustomGraphicElement';
import { Theme } from '../entities/Drawable/Theme';
import { BackPattern } from '../entities/Drawable/BackPattern';
import { Graduation } from '../entities/Drawable/Graduation';
import { ElementHandle } from '../entities/ElementHandle';

class Engine {
    constructor() {
        this.content = [];
        this.mediaList = [];
        this.selectedOffset = new Vector(0, 0);
        this.isThereHorizontalBorder = false;
        this.historic = new Historic(this);
        this.preRender = [];
        this.postRender = [];
        Media.engine = this;
    }
    set selectedElement(el) {
        if (el && !el.canBeSelected) {
            return;
        }
        this._selectedElement = el;
    }
    get selectedElement() {
        return this._selectedElement;
    }
    create(type, content, pos = new Position()) {
        let newObj;
        let media;
        switch (type) {
            case 'text':
                newObj = new Text(this, content, pos);
                break;
            case 'image':
                media = Media.fromContent(content.value);
                newObj = new Image(this, media, pos);
                break;
            case 'svg':
                media = Media.fromContent(content.value);
                newObj = new ImageSVG(this, media);
                break;
            case 'theme':
                newObj = new Theme(this, content, pos);
                break;
            case 'backPattern':
                newObj = new BackPattern(this, content, pos);
                break;
            case 'graduation':
                media = Media.fromContent(content);
                newObj = new Graduation(this, media, pos);
                break;
            default:
                newObj = new CustomGraphicElement(this, content);
                newObj.transform.moveTo(pos);
        }
        return newObj;
    }
    delete(uuid) {
        const elementIndex = this.content.findIndex((element) => {
            return element.uuid === uuid;
        });
        if (elementIndex === -1) {
            return;
        }
        this.content.splice(elementIndex, 1);
        if (this.selectedElement && uuid === this.selectedElement.uuid) {
            this.selectedElement = undefined;
        }
        this.canvas.removeElement([uuid]);
    }
    moveLayer(uuid, layer) {
        const elementIndex = this.getElementIndex(uuid);
        if (elementIndex == -1) {
            return;
        }
        const copyElement = this.content.splice(elementIndex, 1)[0]; //[element]
        let targetIndex;
        switch (layer) {
            case 'forward':
                targetIndex = this.isInFront(elementIndex) ? elementIndex : elementIndex + 1;
                break;
            case 'backward':
                targetIndex = this.isBehind(elementIndex) ? elementIndex : elementIndex - 1;
                break;
            case 'back':
                targetIndex = 0;
                break;
            case 'front':
                targetIndex = this.content.length;
                break;
            default:
                targetIndex = elementIndex;
                break;
        }
        this.content.splice(targetIndex, 0, copyElement);
    }
    getElementIndex(uuid) {
        return this.content.findIndex((element) => {
            return element.uuid === uuid;
        });
    }
    isInFront(elementIndex) {
        return elementIndex === this.content.length;
    }
    isBehind(elementIndex) {
        return elementIndex === 0;
    }
    reorderElement(uuid, targetIndex) {
        const elementIndex = this.getElementIndex(uuid);
        if (elementIndex == -1) {
            return;
        }
        const copyElement = this.content.splice(elementIndex, 1)[0]; //[element]
        targetIndex = targetIndex < 0 ? 0 : targetIndex;
        targetIndex = targetIndex > this.content.length ? this.content.length : targetIndex;
        this.content.splice(targetIndex, 0, copyElement);
    }
    get canvas() {
        return this._canvas;
    }
    set canvas(canvas) {
        this._canvas = canvas;
        this._canvas.engine = this;
    }
    static getInstance() {
        if (!Engine.instances[Engine.currentInstance]) {
            Engine.instances[Engine.currentInstance] = new Engine();
        }
        const instance = Engine.instances[Engine.currentInstance];
        // Handle de debug (TEMPORAIRE) : Engine n'est pas une variable globale, ce qui
        // empeche d'inspecter le canvas Fabric depuis la console (diagnostic thèmes
        // importés : masques/clip). Aucun effet fonctionnel. À RETIRER une fois le
        // diagnostic « photo masquée absente » terminé. Accès console : `__c3d.canvas.canvas`.
        if (typeof window !== 'undefined') {
            (window as any).__c3d = instance;
        }
        return instance;
    }
    get widthCanvas() {
        // _canvas est un FabricWrapper, donc on accède à .canvas.width
        return this._canvas?.canvas?.width || 1;
    }
    get heightCanvas() {
        // _canvas est un FabricWrapper, donc on accède à .canvas.height
        return this._canvas?.canvas?.height || 1;
    }
    find(uuid) {
        for (const el of this.content) {
            if (el instanceof Theme) {
                for (const GE of el.content) {
                    if (GE.uuid === uuid) {
                        return GE;
                    }
                }
            }
            if (el instanceof BackPattern) {
                if (el.content.uuid === uuid) {
                    return el.content;
                }
            }
            if (el.uuid === uuid) {
                return el;
            }
        }
        throw `not found ${uuid}`;
    }
    // TODO: isInRect no return??
    updateSelectedElement(mousePosition) {
        const objects = [...this.content];
        //if (typeof this.selectedElement !== 'undefined')
        //objects.push(this.selectedElement); // test selected element first
        let i = objects.length - 1;
        while (i >= 0) {
            const el = objects[i];
            if (!el.canBeSelected) {
                i--;
                continue;
            }
            if (el.isSelected) {
                if (this.processSelectionHandle(el, mousePosition)) {
                    return;
                }
            }
            if (this.processSelectionElement(el, mousePosition)) {
                return;
            }
            i--;
        }
        this.selectedElement = undefined;
    }
    processSelectionElement(el, mousePosition) {
        if (el.isInside(mousePosition)) {
            this.selectedElement = el;
            this.selectedOffset = new Vector(0, 0);
            return true;
        }
        if (this.isThereHorizontalBorder || !el.isDuplicated) {
            return false;
        }
        let offset = new Vector(-this.widthCanvas, 0);
        if (el.isInside(mousePosition.clone().applyVector(offset))) {
            this.selectedElement = el;
            this.selectedOffset = offset;
            return true;
        }
        offset = new Vector(this.widthCanvas, 0);
        if (el.isInside(mousePosition.clone().applyVector(offset))) {
            this.selectedElement = el;
            this.selectedOffset = offset;
            return true;
        }
        return false;
    }
    processSelectionHandle(el, mousePosition) {
        let offset;
        offset = new Vector(0, 0);
        el.selectHandle(mousePosition);
        if (el.selectedHandle) {
            this.selectedOffset = offset;
            return true;
        }
        if (this.isThereHorizontalBorder || !el.isDuplicated) {
            return false;
        }
        offset = new Vector(-this.widthCanvas, 0);
        el.selectHandle(mousePosition.clone().applyVector(offset));
        if (el.selectedHandle) {
            this.selectedOffset = offset;
            return true;
        }
        offset = new Vector(this.widthCanvas, 0);
        el.selectHandle(mousePosition.clone().applyVector(offset));
        if (el.selectedHandle) {
            this.selectedOffset = offset;
            return true;
        }
    }
    unselectElement() {
        this.selectedElement = undefined;
    }
    /**
     * 🎯 FIX: Calcule les limites de la zone de personnalisation
     *
     * ✅ AVEC CANVAS DYNAMIQUE: Le canvas est déjà adapté aux dimensions du produit
     * Donc la zone de personnalisation = TOUT le canvas (left: 0, top: 0)
     */
    getCustomizationAreaBounds() {
        // ✅ Le canvas est maintenant dimensionné exactement à la zone de personnalisation
        // Plus besoin de calculer d'offset, toute la surface du canvas est utilisable
        return {
            left: 0,
            top: 0,
            width: this.widthCanvas || 2048,
            height: this.heightCanvas || 2048
        };
    }
    static get currentInstance() {
        return this._currentInstance;
    }
    static set currentInstance(instance) {
        this._currentInstance = instance;
        Media.engine = Engine.getInstance();
    }
    set handleSize(size) {
        ElementHandle.size = size;
    }
}
Engine._currentInstance = 'main';
Engine.instances = {};
export { Engine };
